/**
 * Acceso de las personas, dentro del panel de usuarios.
 *
 * Dos piezas, que son las dos mitades del mismo problema:
 *
 * `FormularioDeInvitacion`
 *     Dar de alta a alguien y entregarle el enlace. Reemplaza al «Dar de alta en
 *     el ERP» que mandaba al formulario generico: ese creaba la ficha y dejaba a
 *     la persona sin poder entrar, que es exactamente el agujero que esto cierra.
 *
 * `PanelDeAcceso`
 *     Para quien ya esta cargado: si puede entrar o esta pendiente, y los dos
 *     botones que el administrador necesita cuando suena el telefono —«reenviar
 *     la invitacion» y «restablecer la contrasena»—.
 *
 * La regla de UX que las gobierna: **el enlace se muestra grande y con boton de
 * copiar, siempre**. No se esconde detras de «ver detalles» ni se trata como el
 * plan B de un mail que no salio. El dia de la entrega puede no haber servidor
 * de correo, y ese es el camino que se va a usar.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge, Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import type { RecordData } from '@/lib/types';
import { estadoDeCuenta } from '@/panel/acceso';
import type { PanelUser } from '@/panel/api';
import { CLAVES } from '@/panel/queries';
import { Casilla, Panel, Vacio } from '@/panel/ui';
import type { Role } from '@/panel/contract';
import {
  AccesoError,
  forzarRestablecimiento,
  invitar,
  leerPadronDeAcceso,
  type EstadoDeAcceso,
  type Invitacion,
  type PadronDeAcceso,
} from '@/views/acceso/api';
import { EnlaceCopiable } from '@/views/acceso/piezas';
import { BotonBloqueado } from '@/views/MotivoBloqueo';

export const CLAVE_PADRON_ACCESO = ['panel', 'acceso', 'estado'] as const;

/** Estado de acceso de todas las personas de la organización. */
export function useEstadoDeAcceso() {
  return useQuery<PadronDeAcceso>({
    queryKey: CLAVE_PADRON_ACCESO,
    queryFn: () => leerPadronDeAcceso(),
    staleTime: 15_000,
  });
}

function mensajeDe(error: unknown): string {
  if (error instanceof AccesoError) return error.message;
  if (error instanceof Error) return error.message;
  return 'El servidor rechazó la operación.';
}

function fecha(iso: string): string {
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// El resultado: el enlace listo para mandar
// ---------------------------------------------------------------------------
function ResultadoDeInvitacion({ emision }: { emision: Invitacion }) {
  const esInvitacion = emision.purpose === 'invitation';
  return (
    <div className="space-y-3 rounded-lg border border-success/30 bg-success-soft p-4">
      <div>
        <p className="text-sm font-semibold">
          {esInvitacion
            ? `Invitación lista para ${emision.name || emision.email}`
            : `Restablecimiento listo para ${emision.name || emision.email}`}
        </p>
        <p className="mt-0.5 text-xs text-fg-muted">
          {emision.email_sent
            ? `Le mandamos el enlace por mail a ${emision.email}. Igual podés pasárselo vos:`
            : 'Pasale este enlace por WhatsApp, por mensaje o como te quede cómodo:'}
        </p>
      </div>

      {/* Los tres botones que promete el manual (06 §6.1). El mensaje lo redacta
          el backend (`share_text`): nombra a la persona y a la administración,
          que son datos del registro y no de la pantalla. */}
      <EnlaceCopiable
        enlace={emision.link}
        {...(emision.share_text === undefined || emision.share_text === ''
          ? {}
          : { mensaje: emision.share_text })}
      />

      <p className="text-xs text-fg-muted">
        Se puede usar <strong>una sola vez</strong> y vence el {fecha(emision.expires_at)}. Al abrirlo,
        la persona elige su contraseña y ya entra con su email.
      </p>

      {!emision.email_sent && emision.email_error !== '' && (
        <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs">
          <strong>No se envió por mail.</strong> {emision.email_error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alta + invitación
// ---------------------------------------------------------------------------
export function FormularioDeInvitacion({
  roles,
  empresas,
  onCerrar,
}: {
  roles: { rol: Role; id: number }[];
  empresas: RecordData[];
  onCerrar: () => void;
}) {
  const cliente = useQueryClient();
  const padron = useEstadoDeAcceso();

  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [gruposElegidos, setGrupos] = useState<number[]>([]);
  const [empresasElegidas, setEmpresas] = useState<number[]>([]);
  const [emision, setEmision] = useState<Invitacion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hayCorreo = padron.data?.email_configured === true;

  const alta = useMutation({
    mutationFn: () =>
      invitar({
        email: email.trim(),
        name: nombre.trim(),
        nucleo_groups: gruposElegidos,
        companies: empresasElegidas,
      }),
    onSuccess: (respuesta) => {
      setEmision(respuesta);
      setError(null);
      void cliente.invalidateQueries({ queryKey: CLAVES.usuarios });
      void cliente.invalidateQueries({ queryKey: CLAVE_PADRON_ACCESO });
      notify.success('Persona creada', `Ya podés pasarle el enlace a ${respuesta.email}.`);
    },
    onError: (fallo: unknown) => {
      setError(mensajeDe(fallo));
    },
  });

  if (emision !== null) {
    return (
      <Panel title="Persona creada" subtitle="Falta un paso: pasarle el enlace">
        <ResultadoDeInvitacion emision={emision} />
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Otra persona: se limpia todo menos las empresas y los roles, que
              // en un alta en tanda suelen repetirse.
              setEmision(null);
              setEmail('');
              setNombre('');
            }}
          >
            Invitar a otra persona
          </Button>
          <Button variant="ghost" size="sm" onClick={onCerrar}>
            Terminar
          </Button>
        </div>
      </Panel>
    );
  }

  const puedeEnviar = email.trim() !== '' && !alta.isPending;

  return (
    <Panel
      title="Invitar a una persona"
      subtitle="Se crea la cuenta y se genera el enlace para que elija su contraseña"
      actions={
        <Button variant="ghost" size="sm" onClick={onCerrar}>
          Cancelar
        </Button>
      }
    >
      {error !== null && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="invitar-email" className="mb-1.5 block text-sm font-medium">
            Email <span className="text-danger">*</span>
          </label>
          <input
            id="invitar-email"
            type="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(evento) => {
              setEmail(evento.target.value);
            }}
            placeholder="nombre@administradora.com.ar"
            className="h-10 w-full rounded-md border border-line bg-surface px-3 text-sm outline-none focus:border-accent"
          />
          <p className="mt-1 text-xs text-fg-faint">Es con lo que va a entrar.</p>
        </div>
        <div>
          <label htmlFor="invitar-nombre" className="mb-1.5 block text-sm font-medium">
            Nombre y apellido
          </label>
          <input
            id="invitar-nombre"
            type="text"
            value={nombre}
            onChange={(evento) => {
              setNombre(evento.target.value);
            }}
            className="h-10 w-full rounded-md border border-line bg-surface px-3 text-sm outline-none focus:border-accent"
          />
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div>
          <p className="mb-2 text-sm font-medium">Roles</p>
          {roles.length === 0 ? (
            <Vacio title="No hay roles asignables" />
          ) : (
            <ul className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
              {roles.map(({ rol, id }) => (
                <li key={rol.code}>
                  <Casilla
                    checked={gruposElegidos.includes(id)}
                    onChange={(valor) => {
                      setGrupos((previo) =>
                        valor ? [...previo, id] : previo.filter((x) => x !== id),
                      );
                    }}
                    title={rol.name}
                    {...(rol.description === '' ? {} : { description: rol.description })}
                  />
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-fg-faint">
            Sin ningún rol, la persona entra pero no ve nada del sistema.
          </p>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Empresas habilitadas</p>
          {empresas.length === 0 ? (
            <Vacio title="No hay empresas cargadas" />
          ) : (
            <ul className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
              {empresas.map((empresa) => (
                <li key={empresa.id}>
                  <Casilla
                    checked={empresasElegidas.includes(empresa.id)}
                    onChange={(valor) => {
                      setEmpresas((previo) =>
                        valor ? [...previo, empresa.id] : previo.filter((x) => x !== empresa.id),
                      );
                    }}
                    title={empresa.display_name}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        <p className="text-xs text-fg-muted">
          {hayCorreo
            ? 'Se le envía el mail y además vas a ver el enlace para copiarlo.'
            : 'Este sistema todavía no tiene servidor de correo: vas a ver el enlace para pasárselo vos.'}
        </p>
        {/* El texto de al lado habla de si hay correo configurado, que es otra
            cosa: lo que frena al botón es que falte el email. Ahora lo dice él. */}
        {puedeEnviar || alta.isPending ? (
          <Button
            variant="primary"
            loading={alta.isPending}
            onClick={() => {
              alta.mutate();
            }}
          >
            Crear e invitar
          </Button>
        ) : (
          <BotonBloqueado
            variant="primary"
            label="Crear e invitar"
            motivo="Falta el email de la persona: es con lo que va a entrar y a dónde se manda el enlace para que elija su contraseña."
          />
        )}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Acceso de una persona ya cargada
// ---------------------------------------------------------------------------
export function PanelDeAcceso({ usuario }: { usuario: PanelUser }) {
  const cliente = useQueryClient();
  const padron = useEstadoDeAcceso();
  const [emision, setEmision] = useState<Invitacion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const estado: EstadoDeAcceso | undefined = padron.data?.results.find(
    (fila) => fila.user_id === usuario.id,
  );

  function alTerminar(respuesta: Invitacion) {
    setEmision(respuesta);
    setError(null);
    void cliente.invalidateQueries({ queryKey: CLAVE_PADRON_ACCESO });
    void cliente.invalidateQueries({ queryKey: CLAVES.usuarios });
  }

  const reenviar = useMutation({
    mutationFn: () => invitar({ user_id: usuario.id }),
    onSuccess: alTerminar,
    onError: (fallo: unknown) => {
      setError(mensajeDe(fallo));
    },
  });

  const restablecer = useMutation({
    mutationFn: () => forzarRestablecimiento(usuario.id),
    onSuccess: alTerminar,
    onError: (fallo: unknown) => {
      setError(mensajeDe(fallo));
    },
  });

  const puedeEntrar = estado?.puede_entrar ?? false;
  const pendiente = estado?.invitacion_pendiente ?? false;
  const trabajando = reenviar.isPending || restablecer.isPending;
  const cuenta = estadoDeCuenta(usuario.is_active, estado?.puede_entrar);

  return (
    <Panel
      title="Acceso"
      subtitle="Si puede entrar, y cómo darle o devolverle la llave"
      actions={
        // Las mismas tres palabras que la lista y la ficha, salidas del mismo
        // lugar: acá decía «Puede entrar» / «Todavía no eligió su contraseña»,
        // que era un tercer vocabulario para la misma pregunta.
        // Ver `estadoDeCuenta`.
        cuenta === null ? undefined : (
          <span className="flex flex-wrap gap-1.5">
            <Badge tone={cuenta.tono} title={cuenta.detalle}>
              {cuenta.etiqueta}
            </Badge>
            {pendiente && <Badge tone="accent">Enlace pendiente</Badge>}
          </span>
        )
      }
    >
      {error !== null && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      {estado !== undefined && (
        <dl className="mb-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            {/* «Último enlace canjeado» y no «eligió su contraseña»: quien tiene
                una clave puesta por el seed o por la consola nunca canjeó nada, y
                decir «todavía no eligió su contraseña» al lado de «Puede entrar»
                se lee como una contradicción. Acá se afirma sólo lo que se sabe. */}
            <dt className="text-xs uppercase tracking-wide text-fg-faint">
              Último enlace canjeado
            </dt>
            <dd>
              {estado.eligio_clave_el === null ? 'Ninguno' : fecha(estado.eligio_clave_el)}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-fg-faint">Enlace pendiente</dt>
            <dd>
              {estado.invitacion_vence_el === null
                ? 'No hay ninguno vigente'
                : `Vence el ${fecha(estado.invitacion_vence_el)}`}
            </dd>
          </div>
        </dl>
      )}

      {emision !== null && (
        <div className="mb-4">
          <ResultadoDeInvitacion emision={emision} />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!puedeEntrar && (
          <Button
            variant="primary"
            size="sm"
            loading={reenviar.isPending}
            disabled={trabajando}
            onClick={() => {
              reenviar.mutate();
            }}
          >
            {pendiente ? 'Generar un enlace nuevo' : 'Invitar a esta persona'}
          </Button>
        )}
        <Button
          variant={puedeEntrar ? 'secondary' : 'ghost'}
          size="sm"
          loading={restablecer.isPending}
          disabled={trabajando}
          onClick={() => {
            restablecer.mutate();
          }}
        >
          Restablecer la contraseña
        </Button>
      </div>

      <p className="mt-3 text-xs text-fg-muted">
        Generar un enlace nuevo <strong>anula el anterior</strong>. La contraseña actual —si la
        tiene— sigue funcionando hasta que la persona canjee el enlace: nadie se queda afuera por
        las dudas.
      </p>
    </Panel>
  );
}
